import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import type {StockItemSnapshot} from '@Libraries/pmw-stock-media-library';
import {StockSources} from '@Libraries/pmw-stock-media-library';
import type {SelectedFilter} from '@Panels/stock-panel/stock-panel.types';
import {AUDIO_CATEGORY_FILTER_IDS, StockButtonFilters, StockPanelTabs} from '@Panels/stock-panel/stock-panel.types';

/**
 * The sources a favorite can have: the stock sources the backend's `StockFavoriteSource`
 * (`Facades/StockFavorites/Enums/StockFavoriteSource.php`) accepts, with the same values because the value is
 * persisted with every favorite. Emoji and Pixabay have no tile that can be favorited, and AI generations are
 * deliberately not favoritable.
 */
export type StockFavoriteSource = Exclude<StockSources, StockSources.PMW_EMOJI | StockSources.PIXABY_IMAGE | StockSources.PIXABY_VIDEO | StockSources.PIXABY_ILLUSTRATION>;

/** What identifies one favorite on the wire, and what the heart map is keyed by. */
export interface StockFavoriteKey {
  itemSource: StockFavoriteSource;
  itemId: string;
}

/**
 * A favorite as the browser knows it before the server does. An outside provider's item carries the grid item
 * to store as its snapshot; PMW and AI items are looked up fresh by id and carry none.
 */
export interface StockFavoriteIdentity extends StockFavoriteKey {
  snapshot?: StockItemSnapshot;
}

/** One favorite the backend hydrated: its key plus the backend data the source's converter takes. */
export interface StockFavoriteBackendItem extends StockFavoriteKey {
  backendData: unknown;
}

export interface StockFavoritesPageResponse {
  items: StockFavoriteBackendItem[];
  hasMore: boolean;
  nextCursor: number | null;
}

/** What narrows a page of favorites, mirrored by the backend's `StockFavoritesQuery`. An absent optional field narrows nothing. */
export interface StockFavoritesQuery {
  /** The only sources the server may return, so it applies the view's and the host's limits before it pages; an empty list matches nothing. */
  sources: StockFavoriteSource[];
  orientation?: string;
  onlyUhd?: boolean;
  /** PMW catalogue categories, any of which matches. */
  categories?: string[];
  /** Storyblocks category ids, all of which a track must have, as the audio search requires them. */
  audioCategoryIds?: number[];
}

/** What a favorites view asks the server for: the view's tab and pills, and the limits of the host that opened the panel. */
export interface StockFavoritesQueryParams {
  tab: StockPanelTabs;
  selectedFilters: SelectedFilter[];
  /** The host's tabs: a tab without sources of its own, such as Favorites, asks for the sources of these tabs. */
  tabsToShow: StockPanelTabs[];
  /** The sources the host blocks: no query asks for them. */
  blacklistedSources: StockSources[];
}

export interface StockFavoritesPageRequest extends StockFavoritesQuery {
  limit: number;
  beforeId: number | null;
}

/** A page's worth of tiles taken off the buffered favorites, and what stays buffered for the next page. */
export interface StockFavoritesProbePage {
  response: GridItemStorage[];
  remainingBuffer: GridItemStorage[];
}

export enum StockFavoritesLoadStatus {
  IDLE = 'IDLE',
  LOADING = 'LOADING',
  LOADED = 'LOADED',
  ERROR = 'ERROR',
}

export interface StockFavoritesState {
  loadStatus: StockFavoritesLoadStatus;
  /** Keyed by the item's source and id joined with a colon; a present key means the item is a favorite. */
  favoriteKeys: Record<string, true>;
  /** Keyed like `favoriteKeys`; a present key means a toggle for that item is in flight. */
  pendingKeys: Record<string, true>;
}

/** The sources whose favorites a media tab's pill shows. The Favorites tab has no entry here: it shows the sources of every tab its host shows. */
export const FAVORITES_PILL_SOURCES_BY_TAB: Partial<Record<StockPanelTabs, StockFavoriteSource[]>> = {
  [StockPanelTabs.PHOTOS]: [StockSources.GETTY_IMAGE, StockSources.STORYBLOCK_IMAGE, StockSources.PMW_STOCK_IMAGE],
  [StockPanelTabs.VIDEOS]: [StockSources.GETTY_VIDEO, StockSources.STORYBLOCK_VIDEO],
  [StockPanelTabs.STICKERS]: [
    StockSources.PMW_STILL_STICKER,
    StockSources.PMW_ANIMATED_STICKER,
    StockSources.PMW_AI_STICKER,
    StockSources.PMW_AI_ANIMATED_STICKER,
    StockSources.PMW_EXTRACTED_GETTY_STICKER,
    StockSources.GETTY_ILLUSTRATION,
    StockSources.STORYBLOCK_ILLUSTRATION,
  ],
  [StockPanelTabs.ICONS]: [StockSources.PMW_ICON, StockSources.PMW_AI_ICON],
  [StockPanelTabs.SHAPES]: [StockSources.PMW_SHAPE],
  [StockPanelTabs.AUDIOS]: [StockSources.STORYBLOCK_AUDIO],
};

/**
 * The filter ids of the pills that narrow the favorites view instead of excluding it, because a favorite stores what
 * they filter on: its source (Stock source, Animated / Still), its orientation, its 4K flag, its catalogue category or
 * a track's Storyblocks categories (the audio dropdowns). Every other pill excludes the favorites pill: the seasonal
 * pill, and any pill added later.
 */
export const FAVORITES_COMPATIBLE_FILTERS: readonly string[] = [
  StockButtonFilters.PRICING,
  StockButtonFilters.ANIMATION,
  StockButtonFilters.ORIENTATION,
  StockButtonFilters.QUALITY,
  StockButtonFilters.CATEGORY,
  ...AUDIO_CATEGORY_FILTER_IDS,
];

/** The compatible pills that narrow the favorites view by picking sources, the same way they pick the search's sources. */
export const FAVORITES_SOURCE_FILTERS: readonly string[] = [StockButtonFilters.PRICING, StockButtonFilters.ANIMATION];

/** Rows fetched per request while filling a grid page; the backend caps a request at 100. */
export const FAVORITES_PAGE_SIZE = 30;

export const FAVORITES_LIMIT_REACHED_ERROR_CODE = 'favorites_limit_reached';
